import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { Check, ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useAuth } from '../App';
import EmailVerifyGate from '../components/EmailVerifyGate';

const PRICING_STAGE = 'https://static.prod-images.emergentagent.com/jobs/e4fc18b8-6f91-4eff-a50b-31c7d916e998/images/79cd5ce7282fdb6e5f79cf909350fc9d34e6bfa4c0db80e92bd7d80dd9c35f10.png';

const DISPLAY = "font-['Cabinet_Grotesk',_'Inter',_sans-serif] font-black tracking-tighter";
const BODY = "font-['Satoshi',_'Inter',_sans-serif]";
const EASE_OUT = [0.16, 1, 0.3, 1];

const PRO_FEATURES = [
  'All 17 themes, 12 fonts, 11 wallpapers',
  'Custom accent colour & animations',
  'Tip Jar (PayPal)',
  'Fan Page feed',
  'Visitor analytics + country map',
  'Profile music player',
  'Countdown + email-signup widgets',
  'Timed links (auto show/hide)',
  'Remove LinkMonkey branding',
  'Priority support',
];

const FREE_FEATURES = [
  'Unlimited links (63 platforms)',
  'Basic themes (3)',
  'QR code generator',
  'Click counter',
  '1 embedded link',
  '"Powered by LinkMonkey" branding',
];

const Reveal = ({ children, delay = 0, y = 24, immediate = false }) => (
  <motion.div
    initial={immediate ? false : { opacity: 0, y }}
    animate={immediate ? { opacity: 1, y: 0 } : undefined}
    whileInView={immediate ? undefined : { opacity: 1, y: 0 }}
    viewport={immediate ? undefined : { once: true, margin: '-60px' }}
    transition={{ duration: 0.9, ease: EASE_OUT, delay }}
  >{children}</motion.div>
);

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let rafId;
    const raf = (t) => { lenis.raf(t); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);
};

const Pricing = () => {
  useLenis();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);

  const isPro = user?.subscription?.is_pro;
  const trialUsed = user?.trial_used;
  const isOnTrial = user?.subscription?.billing_cycle === 'trial';
  const canStartTrial = !user || (!isPro && !trialUsed);

  const handleUpgrade = async () => {
    if (!user) { navigate('/signup?next=pricing'); return; }
    if (user.email_verified === false) {
      toast.error('Please verify your email before upgrading. Check your inbox for the verification link.');
      return;
    }
    setLoading(true);
    try {
      const { url } = await api.createCheckout(cycle);
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Checkout failed');
      setLoading(false);
    }
  };

  const handleTrial = async () => {
    if (!user) { navigate('/signup?next=pricing&trial=1'); return; }
    if (user.email_verified === false) {
      toast.error('Please verify your email before starting your trial. Check your inbox for the verification link.');
      return;
    }
    setTrialLoading(true);
    try {
      await api.startTrial();
      toast.success('Your 7-day Pro trial is active!');
      setTimeout(() => window.location.href = '/dashboard', 800);
    } catch (e) {
      toast.error(e.message || 'Could not start trial');
      setTrialLoading(false);
    }
  };

  const monthlyPrice = 4;
  const yearlyPrice = 40;
  const yearlyMonthly = (yearlyPrice / 12).toFixed(2);
  const savings = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100);

  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid="pricing-page">
      <Helmet>
        <title>Pricing — LinkMonkey Pro · €4/month, full access</title>
        <meta name="description" content="Free forever for the essentials. Upgrade to Pro for €4/mo for full customisation, analytics, monetisation and more. 7-day free trial." />
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Soft blue radial wash */}
      <div className="fixed inset-0 -z-20 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(0,85,255,0.07) 0%, transparent 65%)' }} />

      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 border-b border-gray-200 bg-white/90">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <MonkeyLogo size={28} />
            <span className={`${BODY} text-[15px] font-semibold text-gray-950`}>LinkMonkey</span>
          </Link>
          <Link to={user ? '/dashboard' : '/login'} className={`${BODY} text-[13px] text-gray-600 hover:text-gray-950 transition-colors`}>
            {user ? 'Dashboard' : 'Log in'}
          </Link>
        </div>
      </nav>

      <main className="relative max-w-6xl mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-32">
        <Reveal immediate>
          <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-5 text-center`}>Pricing</p>
        </Reveal>
        <Reveal immediate delay={0.05}>
          <h1 className={`${DISPLAY} text-center text-[clamp(3rem,8vw,7rem)] leading-[0.92] mb-6 text-gray-950`}>
            Invest in your<br />
            <span className="italic font-light text-gray-500" style={{ fontFamily: "'Cabinet Grotesk', serif" }}>
              digital real estate.
            </span>
          </h1>
        </Reveal>
        <Reveal immediate delay={0.1}>
          <p className={`${BODY} text-center text-gray-600 text-base md:text-lg max-w-xl mx-auto mb-12`}>
            Free forever for the essentials. €{monthlyPrice}/month for everything that makes your page stand out.
          </p>
        </Reveal>

        {/* Billing toggle */}
        <Reveal delay={0.15}>
          <div className="flex justify-center mb-14">
            <div className={`${BODY} inline-flex items-center gap-1 p-1 rounded-full border border-gray-200 bg-white shadow-sm`} data-testid="billing-toggle">
              <button onClick={() => setCycle('monthly')}
                className={`px-5 py-2 text-[13px] font-semibold rounded-full transition-colors ${cycle === 'monthly' ? 'bg-gray-950 text-white' : 'text-gray-600 hover:text-gray-950'}`}
                data-testid="toggle-monthly">
                Monthly
              </button>
              <button onClick={() => setCycle('yearly')}
                className={`relative px-5 py-2 text-[13px] font-semibold rounded-full transition-colors ${cycle === 'yearly' ? 'bg-gray-950 text-white' : 'text-gray-600 hover:text-gray-950'}`}
                data-testid="toggle-yearly">
                Yearly
                <span className={`absolute -top-2 -right-3 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cycle === 'yearly' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                  -{savings}%
                </span>
              </button>
            </div>
          </div>
        </Reveal>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 relative">
          {/* FREE */}
          <Reveal delay={0.2}>
            <div className="rounded-3xl border border-gray-200 bg-white shadow-sm p-8 md:p-10 h-full" data-testid="plan-free">
              <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-4`}>Free</p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`${DISPLAY} text-6xl text-gray-950`}>€0</span>
                <span className={`${BODY} text-gray-500 text-sm`}>forever</span>
              </div>
              <p className={`${BODY} text-gray-600 text-[13.5px] mb-8`}>Everything you need to launch.</p>

              <Link to={user ? '/dashboard' : '/signup'}>
                <button className={`${BODY} w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-gray-900 px-5 py-3 rounded-full border border-gray-200 bg-white hover:bg-[#C9DEF9] hover:border-gray-300 transition-colors mb-8`}
                  data-testid="free-cta-btn">
                  {user ? 'Go to dashboard' : 'Get started'}
                </button>
              </Link>

              <div className="space-y-3">
                {FREE_FEATURES.map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className={`${BODY} text-[14px] text-gray-700`}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* PRO — tracing beam + stage spotlight */}
          <Reveal delay={0.3}>
            <div className="relative h-full" data-testid="plan-pro">
              {/* Tracing beam border */}
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-white/40 opacity-80 pointer-events-none" />
              {/* Stage spotlight bg */}
              <div
                className="absolute inset-px rounded-3xl overflow-hidden pointer-events-none"
                style={{ backgroundImage: `url(${PRICING_STAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.35 }}
              />
              <div className="relative rounded-3xl bg-[#06070C] p-8 md:p-10 h-full overflow-hidden">
                <div className="absolute top-5 right-5">
                  <span className={`${BODY} inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black bg-white px-2.5 py-1 rounded-full`}>
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </span>
                </div>

                <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-white/50 mb-4`}>LinkMonkey Pro</p>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={`${DISPLAY} text-6xl text-white`}>€{cycle === 'monthly' ? monthlyPrice : yearlyMonthly}</span>
                  <span className={`${BODY} text-white/50 text-sm`}>/month</span>
                </div>
                <p className={`${BODY} text-white/60 text-[13.5px] mb-8`}>
                  {cycle === 'monthly'
                    ? 'Auto-renews monthly. Cancel anytime in one click.'
                    : `€${yearlyPrice} billed yearly — auto-renews, save €${(monthlyPrice * 12) - yearlyPrice}.`}
                </p>

                {isPro ? (
                  <button disabled
                    className={`${BODY} w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-5 py-3 rounded-full mb-8 cursor-default`}
                    data-testid="already-pro">
                    <Check className="w-4 h-4" /> You're on Pro {isOnTrial && '(Trial)'}
                  </button>
                ) : (
                  <div className="mb-8 space-y-2.5">
                    <EmailVerifyGate user={user} theme="dark" />
                    {canStartTrial && (
                      <button onClick={handleTrial} disabled={trialLoading}
                        className={`${BODY} w-full inline-flex items-center justify-center gap-2 text-sm font-semibold bg-white text-black px-5 py-3 rounded-full hover:bg-white/90 transition-all hover:scale-[1.02] disabled:opacity-60`}
                        data-testid="trial-btn">
                        {trialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start 7-day free trial <ArrowRight className="w-4 h-4" /></>}
                      </button>
                    )}
                    <button onClick={handleUpgrade} disabled={loading}
                      className={`${BODY} w-full inline-flex items-center justify-center gap-2 text-sm font-semibold px-5 py-3 rounded-full transition-all disabled:opacity-60 ${
                        canStartTrial
                          ? 'text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/30'
                          : 'bg-white text-black hover:bg-white/90 hover:scale-[1.02]'
                      }`}
                      data-testid="upgrade-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{canStartTrial ? 'Skip trial — subscribe now' : 'Subscribe to Pro'} <ArrowRight className="w-4 h-4" /></>}
                    </button>
                    {canStartTrial && (
                      <p className={`${BODY} text-[11px] text-white/40 text-center pt-1`}>No card required for trial.</p>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  {PRO_FEATURES.map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span className={`${BODY} text-[14px] text-white/90`}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-28 max-w-2xl mx-auto">
          <Reveal>
            <h2 className={`${DISPLAY} text-gray-950 text-3xl md:text-4xl tracking-tight mb-10 text-center`}>Questions</h2>
          </Reveal>
          <div className="space-y-1">
            {[
              { q: 'Can I switch from Free to Pro anytime?', a: 'Yes — upgrade in one click and your existing settings carry over. Cancel anytime from your Billing page.' },
              { q: 'What happens if I downgrade?', a: 'Pro-only features (custom themes, Tip Jar, Fan Page, etc.) are hidden but never deleted. Re-upgrade to restore them instantly.' },
              { q: 'Will my price ever increase?', a: 'No — €4/month is locked in for life once you subscribe. The more people who join, the lower we keep it.' },
              { q: 'How is payment handled?', a: 'Stripe Checkout — same payment processor used by Apple, Amazon and Shopify. We never see your card details.' },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-b border-gray-200 py-6">
                  <p className={`${BODY} text-[15px] font-semibold text-gray-900 mb-2`}>{item.q}</p>
                  <p className={`${BODY} text-[14px] text-gray-600 leading-relaxed`}>{item.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pricing;
